import axios from "axios";
import type { UnknownAction } from "@reduxjs/toolkit";
import { setLicenceValidity } from "../reducers/licence";
import { showToastNotification } from "../reducers/toastSlice";
import { store } from "../ProviderApp";
import { getBackendUrl } from "@Utils";
import { resolveErrorContent } from "@i18n";
import { createAxiosWrapper, routeCaller, PlatformAdapter, ApiResponse, type ApiPostPath } from "./axiosShared";
import { TOAST_TYPE_ERROR } from "./constants";
import { webPlatformStatus } from "@Features/platform-status/core/webPlatformStatus";

const webAdapter: PlatformAdapter = {
  storage: {
    getItem: (key) => localStorage.getItem(key),
    removeItem: (key) => localStorage.removeItem(key),
  },
  navigation: {
    getCurrentPath: () => window.location.pathname,
    redirectToLogin: (params) => {
      const searchParams = new URLSearchParams();
      if (params?.redirectUrl) {
        searchParams.set("redirectUrl", encodeURIComponent(params.redirectUrl + window.location.search));
      }
      if (params?.errorMessage) {
        searchParams.set("errorMessage", encodeURIComponent(params.errorMessage));
      }
      window.location.href = `/?${searchParams.toString()}${window.location.hash}`;
    },
  },
  // The shared adapter declares `action` as `unknown` so mobile can plug its own store in. The only
  // things ever handed to it are this adapter's own two producers below — `{ type: "LOGOUT" }` and
  // `setLicenceValidity(false)` — dispatched by axiosShared.ts (the two `adapter.dispatch(
  // adapter.actions.…())` calls), so it is always an RTK action object. Fix: declare
  // `PlatformAdapter.dispatch`/`actions` in axiosShared.ts as `UnknownAction` and drop this.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above: the two producers in `actions` are the only callers' input and both return RTK action objects
  dispatch: (action) => store.dispatch(action as UnknownAction),
  actions: {
    setLoggedOut: () => ({ type: "LOGOUT" }),
    setLicenceInvalid: () => setLicenceValidity(false),
    // axiosShared hands over the failure body, not text — resolving a code to a translated sentence
    // is a platform concern, which is what keeps that shared file free of any i18n import.
    showToast: (body) => showToastNotification({ content: resolveErrorContent(body), type: TOAST_TYPE_ERROR }),
  },
  excludedPathsFor401: ["/95789", "/signup", "/thank-you", "/pdfExport", "/"],
  getBackendUrl,
  isAuthenticated: () => Boolean(store.getState().checkIfAuthenticated?.data.status),
  onBackendReachability: webPlatformStatus.reportBackendReachability,
};

const { instance, typedAxios } = createAxiosWrapper(webAdapter, axios);

export type { ApiResponse };
export type { ApiPostPath, ApiPostRequest, ApiPostResponse } from "./axiosShared";
export { typedAxios };
// Raw axios instance (interceptors already attached). Exposed so lifecycle-aware wrappers such as
// useAbortableApi can delegate to it while re-declaring the TypedAxiosInstance surface for callers.
export const axiosInstance = instance;
export const axiosWrapper = typedAxios;
export default typedAxios;

/** A route's caller with the route fixed (see `routeCaller`). A declaration, so it is usable at module scope. */
export function postTo<P extends ApiPostPath>(path: P) {
  return routeCaller(() => typedAxios, path);
}
